import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Bookmark,
  Clock,
  Heart,
  Info,
  Map,
  MapPinned,
  Navigation,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { trackProductEvent } from "../analytics/trackEvent";
import DesktopRightRail from "../components/DesktopRightRail";
import FavoriteSpotFishingSetting from "../components/favoriteSpots/FavoriteSpotFishingSetting";
import "../styles/pages/FavoriteSpots.css";

function formatSpotCoordinate(value) {
  return Number.isFinite(value) ? value.toFixed(3) : value;
}

export default function FavoriteSpotsPage({
  favoriteSpots = [],
  onOpenSpot,
  currentUser,
  posts = [],
  users = [],
  onOpenUser,
  enableDesktopRightRail = false,
  desktopRightRailTargetId,
  canAccessFishingOutlook = false,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const whySaveRef = useRef(null);
  const [desktopRightRailTarget, setDesktopRightRailTarget] = useState(null);
  const [displayedFavoriteSpots, setDisplayedFavoriteSpots] = useState(
    favoriteSpots
  );
  const savedCount = displayedFavoriteSpots.length;

  useEffect(() => {
    setDisplayedFavoriteSpots(favoriteSpots);
  }, [favoriteSpots]);

  useEffect(() => {
    if (!desktopRightRailTargetId || typeof document === "undefined") {
      setDesktopRightRailTarget(null);
      return;
    }

    setDesktopRightRailTarget(
      document.getElementById(desktopRightRailTargetId)
    );
  }, [desktopRightRailTargetId]);

  const closeUtilityPage = useCallback(() => {
    if (
      typeof window !== "undefined" &&
      window.history?.state &&
      Number(window.history.state.idx) > 0
    ) {
      navigate(-1);
      return;
    }

    navigate("/", { replace: true });
  }, [navigate]);

  const openMap = () => {
    navigate({
      pathname: "/",
      search: "?tab=map",
      hash: "",
    });
  };

  const scrollToWhySave = () => {
    whySaveRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const openFavoriteSpot = (spot) => {
    trackProductEvent("map_favorite_spot_opened", {
      featureArea: "map",
      metadata: {
        source: "favorite_spots_page",
        has_location_label: Boolean(spot?.location || spot?.name),
      },
    });
    onOpenSpot?.(spot);
  };

  const desktopRightRail = enableDesktopRightRail ? (
    <DesktopRightRail
      currentUser={currentUser}
      posts={posts}
      users={users}
      onOpenUser={onOpenUser}
    />
  ) : null;

  return (
    <div
      className={`favorite-spots-shell${
        enableDesktopRightRail ? " favorite-spots-shell--with-right-rail" : ""
      }`}
    >
      <section className="favorite-spots-page">
      <header className="favorite-spots-intro">
        <span className="favorite-spots-intro__icon" aria-hidden="true">
          <MapPinned size={24} strokeWidth={2.1} />
        </span>
        <div className="favorite-spots-intro__copy">
          <h1>{t("favorite_spots")}</h1>
          <p>{t("favorite_spots_subtitle")}</p>
        </div>
        <button
          type="button"
          className="favorite-spots-close"
          onClick={closeUtilityPage}
          aria-label={t("common_close")}
        >
          <X size={18} strokeWidth={2.5} aria-hidden="true" />
        </button>
      </header>

      <section className="favorite-spots-hero">
        <img
          src="/banner/favorite-spots.png"
          alt=""
          aria-hidden="true"
          className="favorite-spots-hero__image favorite-spots-hero__image--light"
        />
        <img
          src="/banner/Darkmode/favorite-spots-hero-dark.png"
          alt=""
          aria-hidden="true"
          className="favorite-spots-hero__image favorite-spots-hero__image--dark"
        />
        <div className="favorite-spots-hero__copy">
          <h2>{t("favorite_spots_hero_title")}</h2>
          <p>{t("favorite_spots_hero_body")}</p>
        </div>
        <div className="favorite-spots-stat" aria-label={t("favorite_spots_stat_label")}>
          <strong>{savedCount}</strong>
          <span>{t("favorite_spots_stat_label")}</span>
        </div>
      </section>

      {savedCount === 0 ? (
        <section className="favorite-spots-empty">
          <span className="favorite-spots-empty__icon" aria-hidden="true">
            <Heart size={40} strokeWidth={1.9} />
            <MapPinned size={28} strokeWidth={2.1} />
          </span>
          <h2>{t("favorite_spots_empty_title")}</h2>
          <p>{t("favorite_spots_empty_body")}</p>
          <div className="favorite-spots-empty__actions">
            <button
              type="button"
              className="favorite-spots-primary"
              onClick={openMap}
            >
              <Map size={18} strokeWidth={2.2} aria-hidden="true" />
              <span>{t("favorite_spots_explore_map")}</span>
            </button>
            <button
              type="button"
              className="favorite-spots-secondary"
              onClick={scrollToWhySave}
            >
              <Info size={17} strokeWidth={2.2} aria-hidden="true" />
              <span>{t("favorite_spots_how_it_works")}</span>
            </button>
          </div>
        </section>
      ) : (
        <div className="favorite-spots-list">
          {displayedFavoriteSpots.map((spot, index) => (
            <article
              className="favorite-spot-card"
              key={
                spot.spotKey ||
                `${spot.name || "spot"}-${spot.lat || ""}-${spot.lng || ""}-${index}`
              }
            >
              <span className="favorite-spot-card__icon" aria-hidden="true">
                <MapPinned size={22} strokeWidth={2.15} />
              </span>

              <div className="favorite-spot-card__body">
                <h2>{spot.name || t("favorite_spots")}</h2>
                {spot.location ? <p>{spot.location}</p> : null}
                <span className="favorite-spot-card__coords">
                  {formatSpotCoordinate(spot.lat)}, {formatSpotCoordinate(spot.lng)}
                </span>
              </div>

              <button
                type="button"
                className="favorite-spot-card__action"
                onClick={() => openFavoriteSpot(spot)}
              >
                <Navigation size={17} strokeWidth={2.2} aria-hidden="true" />
                <span>{t("open_location")}</span>
              </button>

              {canAccessFishingOutlook && spot.spotKey ? (
                <FavoriteSpotFishingSetting
                  spot={spot}
                  onSaved={(updatedSpot) => {
                    setDisplayedFavoriteSpots((current) =>
                      current.map((candidate) =>
                        candidate.spotKey === updatedSpot.spotKey
                          ? updatedSpot
                          : candidate
                      )
                    );
                  }}
                />
              ) : null}
            </article>
          ))}
        </div>
      )}

      <section className="favorite-spots-why" ref={whySaveRef}>
        <h2>{t("favorite_spots_why_title")}</h2>
        <div className="favorite-spots-why__grid">
          <article className="favorite-spots-info-card">
            <span aria-hidden="true">
              <Bookmark size={20} strokeWidth={2.15} />
            </span>
            <div>
              <h3>{t("favorite_spots_info_saved_title")}</h3>
              <p>{t("favorite_spots_info_saved_body")}</p>
            </div>
            <strong>
              {t("favorite_spots_count_pill", { count: savedCount })}
            </strong>
          </article>

          <article className="favorite-spots-info-card">
            <span aria-hidden="true">
              <Navigation size={20} strokeWidth={2.15} />
            </span>
            <div>
              <h3>{t("favorite_spots_info_access_title")}</h3>
              <p>{t("favorite_spots_info_access_body")}</p>
            </div>
            <strong>{t("favorite_spots_access_pill")}</strong>
          </article>

          <article className="favorite-spots-info-card">
            <span aria-hidden="true">
              <Clock size={20} strokeWidth={2.15} />
            </span>
            <div>
              <h3>{t("favorite_spots_info_recent_title")}</h3>
              <p>{t("favorite_spots_info_recent_body")}</p>
            </div>
            <strong>{t("favorite_spots_recent_pill")}</strong>
          </article>
        </div>
      </section>

      <div className="favorite-spots-bottom-actions">
        <button
          type="button"
          className="favorite-spots-secondary favorite-spots-bottom-close"
          onClick={closeUtilityPage}
        >
          {t("common_close")}
        </button>
      </div>
      </section>

      {desktopRightRail && desktopRightRailTarget
        ? createPortal(desktopRightRail, desktopRightRailTarget)
        : desktopRightRail && !desktopRightRailTargetId
          ? desktopRightRail
          : null}
    </div>
  );
}
